import { UzButton, UzNumberInput } from '@utazzo/ui';
import { Plus, Trash2, Users } from 'lucide-react';
import React from 'react';

import { resizeChildAges, type Room, type RoomPlan } from './useRoomPlan';

const RoomRow = ({
  room,
  index,
  showLabels,
  disabled,
  canRemove,
  maxAdults,
  maxChildren,
  maxInfants,
  showChildNoBed,
  showChildAges,
  onChange,
  onRemove,
}: {
  room: Room;
  index: number;
  showLabels: boolean;
  disabled: boolean;
  canRemove: boolean;
  maxAdults: number;
  maxChildren: number;
  maxInfants: number;
  /** Child no-bed only applies to the in-house PREFERRED hotel pricing engine;
   *  live TBO prices children by age, so the column is hidden for Live. */
  showChildNoBed: boolean;
  /** Per-child age boxes (live TBO pricing); FD does not need them. */
  showChildAges: boolean;
  onChange: (patch: Partial<Room>) => void;
  onRemove: () => void;
}): React.JSX.Element => (
  <div className="space-y-2.5">
    <div
      className={
        showChildNoBed
          ? 'grid grid-cols-[20px_repeat(4,minmax(0,1fr))_32px] items-end gap-2.5'
          : 'grid grid-cols-[20px_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_32px] items-end gap-2.5'
      }
    >
      <span className="pb-2 text-xs font-medium text-zinc-400">{index + 1}</span>
      <UzNumberInput
        label={showLabels ? 'Adults' : undefined}
        precision={0}
        min={0}
        max={maxAdults}
        value={room.adults}
        onValueChange={(v) => onChange({ adults: v ?? 0 })}
        disabled={disabled}
      />
      {showChildNoBed ? (
        // Bed / no-bed entered directly. Stored as `children` (total) with
        // `childrenNoBed` as the subset, so with-bed = children - noBed.
        <>
          <UzNumberInput
            label={showLabels ? 'Child with bed' : undefined}
            aria-label="Children with bed"
            precision={0}
            min={0}
            max={Math.max(0, maxChildren - (room.childrenNoBed ?? 0))}
            value={room.children - (room.childrenNoBed ?? 0)}
            onValueChange={(v) => onChange({ children: (v ?? 0) + (room.childrenNoBed ?? 0) })}
            disabled={disabled}
          />
          <UzNumberInput
            label={showLabels ? 'Child without bed' : undefined}
            aria-label="Children without bed"
            precision={0}
            min={0}
            max={Math.max(0, maxChildren - (room.children - (room.childrenNoBed ?? 0)))}
            value={room.childrenNoBed ?? 0}
            onValueChange={(v) =>
              onChange({
                children: room.children - (room.childrenNoBed ?? 0) + (v ?? 0),
                childrenNoBed: v ?? 0,
              })
            }
            disabled={disabled}
          />
        </>
      ) : (
        <UzNumberInput
          label={showLabels ? 'Children' : undefined}
          precision={0}
          min={0}
          max={maxChildren}
          value={room.children}
          onValueChange={(v) =>
            onChange({ children: v ?? 0, childrenNoBed: Math.min(room.childrenNoBed ?? 0, v ?? 0) })
          }
          disabled={disabled}
        />
      )}
      <UzNumberInput
        label={showLabels ? 'Infants' : undefined}
        precision={0}
        min={0}
        max={maxInfants}
        value={room.infants}
        onValueChange={(v) => onChange({ infants: v ?? 0 })}
        disabled={disabled}
      />
      <UzButton
        variant="ghost"
        size="icon"
        className="h-9 w-9 text-zinc-400 hover:text-red-600"
        disabled={disabled || !canRemove}
        onClick={onRemove}
        aria-label="Remove room"
      >
        <Trash2 className="h-4 w-4" />
      </UzButton>
    </div>
    {/* Per-child ages - one box per child in this room. Drives live TBO hotel
        child pricing (age bands). Autofilled from the lead when available;
        boxes stay EMPTY (not a fake default) until a real age is entered. */}
    {showChildAges && room.children > 0 && (
      <div className="pl-[30px]">
        <p className="mb-1 text-[10px] tracking-wider text-zinc-400 uppercase">
          {room.children === 1 ? 'Child age' : 'Child ages'}
        </p>
        <div className="flex flex-wrap gap-2">
          {Array.from({ length: room.children }).map((_, i) => (
            <div key={i} className="w-[72px]">
              <UzNumberInput
                aria-label={`Child ${i + 1} age`}
                placeholder="Age"
                precision={0}
                min={0}
                max={17}
                // null / undefined -> empty box (age not recorded yet).
                value={room.childAges?.[i] ?? ''}
                onValueChange={(v) => {
                  const next = resizeChildAges(room.childAges, room.children);
                  next[i] = v === null || v === undefined ? null : Math.max(0, Math.min(17, v));
                  onChange({ childAges: next });
                }}
                disabled={disabled}
              />
            </div>
          ))}
        </div>
      </div>
    )}
  </div>
);

/**
 * Families > rooms editor, shared by the FIT Configure step and the FD config
 * panel. The parent owns the `useRoomPlan` state and any surrounding card.
 */
export const RoomsTravellersEditor = ({
  room,
  disabled,
  showChildNoBed,
  showChildAges = true,
  onTouch,
}: {
  room: RoomPlan;
  disabled: boolean;
  /** Child-without-bed input (PREFERRED pricing and FD). */
  showChildNoBed: boolean;
  /** Per-child age boxes (live TBO pricing); FD does not need them. */
  showChildAges?: boolean;
  /** Called before every edit so the parent can mark itself dirty. */
  onTouch?: () => void;
}): React.JSX.Element => {
  const touch = (): void => onTouch?.();
  return (
    <>
      <div className="space-y-4">
        {room.families.map((fam, fi) => {
          const fAdults = fam.rooms.reduce((s, r) => s + (r.adults || 0), 0);
          const fChildren = fam.rooms.reduce((s, r) => s + (r.children || 0), 0);
          const fInfants = fam.rooms.reduce((s, r) => s + (r.infants || 0), 0);
          return (
            <div
              key={fam.id}
              className={
                room.isMultiFamily ? 'rounded-lg border border-zinc-200 bg-zinc-50/50 p-3.5' : ''
              }
            >
              {room.isMultiFamily && (
                <div className="mb-2.5 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="bg-accent text-accent-foreground inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold">
                      <Users className="h-3 w-3" aria-hidden />
                      Family {fi + 1}
                    </span>
                    <input
                      type="text"
                      value={fam.label ?? ''}
                      maxLength={80}
                      placeholder={`Family ${fi + 1}`}
                      aria-label={`Family ${fi + 1} name`}
                      disabled={disabled}
                      onChange={(e) => {
                        touch();
                        room.setFamilyLabel(fam.id, e.target.value);
                      }}
                      className="h-7 w-36 rounded-md border border-zinc-200 bg-white px-2 text-xs"
                    />
                    <span className="text-xs text-zinc-500">
                      {fAdults}A{fChildren ? ` ${fChildren}C` : ''}
                      {fInfants ? ` ${fInfants}I` : ''} · {fam.rooms.length} room
                      {fam.rooms.length === 1 ? '' : 's'}
                    </span>
                  </div>
                  <UzButton
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-zinc-400 hover:text-red-600"
                    disabled={disabled || room.families.length === 1}
                    onClick={() => {
                      touch();
                      room.removeFamily(fam.id);
                    }}
                    aria-label={`Remove family ${fi + 1}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </UzButton>
                </div>
              )}

              <div className="space-y-2.5">
                {fam.rooms.map((r, ri) => (
                  <RoomRow
                    key={r.id}
                    room={r}
                    index={ri}
                    showLabels={ri === 0}
                    disabled={disabled}
                    canRemove={fam.rooms.length > 1}
                    maxAdults={room.maxFor(r.id, 'adults')}
                    maxChildren={room.maxFor(r.id, 'children')}
                    maxInfants={room.maxFor(r.id, 'infants')}
                    showChildNoBed={showChildNoBed}
                    showChildAges={showChildAges}
                    onChange={(patch) => {
                      touch();
                      room.setRoom(fam.id, r.id, patch);
                    }}
                    onRemove={() => {
                      touch();
                      room.removeRoom(fam.id, r.id);
                    }}
                  />
                ))}
              </div>

              <UzButton
                variant="outline"
                size="sm"
                className="mt-2 h-7 text-xs"
                onClick={() => {
                  touch();
                  room.addRoom(fam.id);
                }}
                disabled={disabled || !room.canAddRoom}
                title={room.canAddRoom ? undefined : 'All travellers are already allocated'}
              >
                <Plus className="mr-1 h-3 w-3" aria-hidden />
                Add room
              </UzButton>
            </div>
          );
        })}
      </div>

      <UzButton
        variant="ghost"
        size="sm"
        className="text-primary mt-1 h-7 text-xs"
        onClick={() => {
          touch();
          room.addFamily();
        }}
        disabled={disabled || !room.canAddFamily}
        title={room.canAddFamily ? undefined : 'All travellers are already allocated'}
      >
        <Plus className="mr-1 h-3 w-3" aria-hidden />
        Add family (split billing)
      </UzButton>
    </>
  );
};
